import { Component } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { useMaterialUIController } from "context";
import { request } from "app/network";
import DataTable from "components/ui/Tables/DataTable";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface SmsHistoryState {
    items: any[];
    loading: boolean;
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    searchPhone: string;

    // Detail Dialog
    detailDialogOpen: boolean;
    selectedItem: any | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_sms_history_page_size";

class SmsHistoryScreenClass extends Component<any, SmsHistoryState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);

        this.state = {
            items: [],
            loading: false,
            totalCount: 0,
            pageNumber: 1,
            pageSize: savedPageSize ? parseInt(savedPageSize, 10) : 20,
            searchPhone: "",
            detailDialogOpen: false,
            selectedItem: null,
        };
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        this.setState({ loading: true });
        try {
            const param = {
                page: this.state.pageNumber,
                pageSize: this.state.pageSize,
                searchPhone: this.state.searchPhone || undefined
            };

            const res = await request("Sms.GetHistory", { paramz: param });

            if (res && res.data) {
                const { items, totalCount } = res.data;
                this.setState({
                    items: items || [],
                    totalCount: totalCount || 0,
                    loading: false
                });
            } else {
                this.setState({ items: [], totalCount: 0, loading: false });
            }
        } catch (error) {
            console.error("Failed to load SMS history:", error);
            this.setState({ loading: false });
        }
    };

    handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchPhone: event.target.value });
    };

    handleSearchSubmit = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            this.setState({ pageNumber: 1 }, this.loadData);
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, this.loadData);
    };

    handleDetailOpen = (item: any) => {
        this.setState({ selectedItem: item, detailDialogOpen: true });
    };

    handleDetailClose = () => {
        this.setState({ detailDialogOpen: false, selectedItem: null });
    };

    getStatusColor = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'SUCCESS': return 'success';
            case 'FAILED': return 'error';
            case 'PENDING': return 'warning';
            default: return 'dark';
        }
    };

    render() {
        const { items, loading, totalCount, pageSize, pageNumber, searchPhone, detailDialogOpen, selectedItem } = this.state;
        const { darkMode } = this.props;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2} textAlign="center">
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const tableColumns = [
            { Header: "ID", accessor: "seqId", width: "7%" },
            { Header: "발송일시", accessor: "sentAt", width: "15%", Cell: ({ value }: any) => renderDateCell(value) },
            { Header: "수신번호", accessor: "toNumber", width: "15%" },
            { Header: "유형", accessor: "requestType", width: "8%" },
            {
                Header: "상태", accessor: "status", width: "10%", Cell: ({ value }: any) => (
                    <MDBox display="flex" alignItems="center">
                        <MDBox
                            width="0.5rem"
                            height="0.5rem"
                            borderRadius="50%"
                            bgColor={this.getStatusColor(value)}
                            mr={1}
                        />
                        <MDTypography variant="caption" fontWeight="medium">{value}</MDTypography>
                    </MDBox>
                )
            },
            {
                Header: "메시지 (미리보기)", accessor: "message", width: "30%", Cell: ({ value }: any) => (
                    <MDTypography variant="caption" color="text" sx={{ display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1 }}>
                        {value}
                    </MDTypography>
                )
            },
            {
                Header: "상세", accessor: "detail", width: "10%", isSorted: false, Cell: ({ row }: any) => (
                    <MDButton variant="text" color="info" onClick={() => this.handleDetailOpen(row.original)}>
                        <Icon>visibility</Icon> 조회
                    </MDButton>
                )
            }
        ];

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">sms</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">SMS 발송 내역</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    <MDInput
                                        placeholder="수신번호 검색"
                                        value={searchPhone}
                                        onChange={this.handleSearchChange}
                                        onKeyDown={this.handleSearchSubmit}
                                        size="small"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: darkMode ? "#ffffff !important" : "inherit" }}>search</Icon>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <MDButton variant="gradient" color="info" onClick={() => { this.setState({ pageNumber: 1 }, this.loadData) }}>
                                        조회
                                    </MDButton>
                                </MDBox>
                            </MDBox>

                            <Divider />

                            <MDBox mb={2}>
                                <MDTypography variant="caption" color="secondary">Total: {totalCount}</MDTypography>
                            </MDBox>

                            {loading ? (
                                <MDBox display="flex" justifyContent="center" p={5}>
                                    <CircularProgress color="info" />
                                </MDBox>
                            ) : (
                                <DataTable
                                    table={{ columns: tableColumns, rows: items }}
                                    showTotalEntries={false}
                                    isSorted={false}
                                    noEndBorder
                                    entriesPerPage={false}
                                    noPagination
                                />
                            )}

                            {totalCount > 0 && (
                                <MDBox mt={3} display="flex" justifyContent="center">
                                    <Pagination
                                        count={Math.ceil(totalCount / pageSize)}
                                        page={pageNumber}
                                        onChange={(_, page) => this.handlePageChange(page - 1)}
                                        color="primary"
                                        showFirstButton
                                        showLastButton
                                    />
                                </MDBox>
                            )}
                        </MDBox>
                    </Card>
                </MDBox>
                <Footer />

                <Dialog open={detailDialogOpen} onClose={this.handleDetailClose} maxWidth="sm" fullWidth>
                    <DialogTitle>SMS 상세 정보</DialogTitle>
                    <DialogContent>
                        {selectedItem && (
                            <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                                <DetailRow label="ID" value={selectedItem.seqId} />
                                <DetailRow label="발송일시" value={formatLocalDateTime(selectedItem.sentAt)} />
                                <DetailRow label="수신번호" value={selectedItem.toNumber} />
                                <DetailRow label="유형" value={selectedItem.requestType} />
                                <DetailRow label="인증코드" value={selectedItem.code || '-'} />
                                <DetailRow label="상태" value={selectedItem.status} />
                                <DetailRow label="실패사유" value={selectedItem.failureReason || '-'} />
                                <Divider />
                                <MDBox>
                                    <MDTypography variant="caption" fontWeight="bold" color="text">메세지 내용</MDTypography>
                                    <MDBox p={2} mt={1} bgColor={darkMode ? "#2c3e50" : "#f5f5f5"} borderRadius="lg">
                                        <MDTypography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedItem.message}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleDetailClose} color="dark">닫기</MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

const DetailRow = ({ label, value }: { label: string, value: any }) => (
    <MDBox display="flex" justifyContent="space-between">
        <MDTypography variant="button" fontWeight="bold" color="text">{label}</MDTypography>
        <MDTypography variant="button" fontWeight="regular">{value}</MDTypography>
    </MDBox>
);

function SmsHistoryScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    return <SmsHistoryScreenClass {...props} darkMode={darkMode} />;
}

export default SmsHistoryScreen;
